import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { EventBusService } from '../../services/event-bus.service';
import * as linkify from 'linkifyjs';

export type MemoType = 'mine' | 'group' | 'commentary';

@Component({
  selector: 'viewer-memo-type',
  template: `
    <ng-container [ngSwitch]="type">
      <ng-container *ngSwitchCase="'mine'">
        <sui-icon class="type-icon" name="메모" type="solid"></sui-icon
        ><span>내 메모</span>
      </ng-container>
      <ng-container *ngSwitchCase="'group'">
        <sui-icon class="type-icon" name="메모" type="solid"></sui-icon
        ><span>공유 메모</span>
      </ng-container>
      <ng-container *ngSwitchCase="'commentary'">
        <sui-icon
          class="type-icon"
          name="코멘터리북(채움)"
          type="solid"
        ></sui-icon
        ><span>코멘테이터 메모</span></ng-container
      >
    </ng-container>
    <sui-icon
      *ngIf="isPinned"
      class="pin-icon"
      name="고정핀"
      type="solid"
    ></sui-icon>
    <div class="new-dot" *ngIf="isUnread"></div>
  `,
})
export class MemoTypeComponent {
  @HostBinding('attr.data-type') @Input() type!: MemoType;
  @Input() isPinned = false;
  @Input() isUnread = false;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[viewer-memo-top-button]',
})
export class MemoTopButtonDirective {}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'viewer-memo-tag',
})
export class MemoTagDirective {}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'viewer-memo-caution',
})
export class MemoCautionDirective {}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'viewer-memo-actions',
})
export class MemoActionsDirective {}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'viewer-memo-reply-comment',
})
export class MemoReplyCommentDirective {}

@Component({
  selector: 'viewer-memo-author',
  template: `<sui-reading-group-member-avatar
      class="avatar"
      [avatarImageURL]="avatarImageURL"
      [isOnline]="isOnline"
      [badgeInfo]="badgeInfo"
      [isMobileBadge]="true"
      (click)="authorClick.emit()"
    >
    </sui-reading-group-member-avatar
    ><span (click)="authorClick.emit()">{{ name }}</span>`,
  // encapsulation: ViewEncapsulation.None,
})
export class MemoAuthorComponent {
  @Input() avatarImageURL?: string;
  @Input() name!: string;
  @Input() isOnline?: boolean;
  @Input() badgeInfo?: {
    type: string;
    name: string;
  };

  @Output() authorClick = new EventEmitter<void>();
}

@Component({
  selector: 'viewer-memo-author-single',
  template: `<sui-reading-group-member-avatar
      class="avatar"
      [avatarImageURL]="avatarImageURL"
      [isOnline]="isOnline"
      (click)="authorClick.emit()"
      [badgeInfo]="badgeInfo"
    >
    </sui-reading-group-member-avatar
    ><span (click)="authorClick.emit()">{{ name }}</span>`,
  // encapsulation: ViewEncapsulation.None,
})
export class MemoAuthorSingleComponent {
  @Input() avatarImageURL?: string;
  @Input() name!: string;
  @Input() isOnline?: boolean;
  @Input() badgeInfo?: {
    type: string;
    name: string;
  };

  @Output() authorClick = new EventEmitter<void>();
}

@Component({
  selector: 'viewer-memo',
  templateUrl: './memo.component.html',
  styleUrls: ['./memo.component.scss'],
})
export class MemoComponent {
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLAnchorElement;

    if (target.tagName === 'A' && target.href) {
      // 하이퍼링크 클릭시 이벤트 버블링 방지 (메모 박스 클릭 이벤트 실행 방지)
      event.stopPropagation();

      // 잘린 URL인 경우 원본 content에서 전체 URL 찾기
      let memoUrl = target.href;
      const clickedUrl = target.href;

      // 원본 content에서 모든 URL 추출
      const links = linkify.find(this._content, 'url');
      if (links && links.length > 0) {
        // 클릭된 URL이 원본 content의 URL 중 하나의 시작 부분과 일치하는지 확인
        const fullUrl = links.find((link: { href?: string; value: string }) => {
          const linkUrl = link.href || link.value;
          // 클릭된 URL이 원본 URL의 시작 부분과 일치하거나, 원본 URL이 클릭된 URL의 시작 부분과 일치하는지 확인
          return (
            linkUrl.startsWith(clickedUrl) || clickedUrl.startsWith(linkUrl)
          );
        });

        if (fullUrl) {
          memoUrl = fullUrl.href || fullUrl.value;
        }
      }

      const regex = /(https?:\/\/[^/:]+(?::\d+)?)\/?(@[^/]+)?/;
      const match = memoUrl.match(regex);
      const domainRegex = /http[s]?:\/\/(?<domain>[^/]+)/;
      const domainMatch = memoUrl.match(domainRegex);

      const origin = window.location.origin; // 현재 호스트 주소
      const urlBid = this.router.url.split('/')[1]; // 현재 URL에 bid 추출

      if (!match) {
        return;
      }

      if (!domainMatch) {
        return;
      }

      if (!domainMatch.groups) {
        return;
      }

      if (
        (origin === match[1] && urlBid === match[2]) ||
        (/(?:^|\.)buk\.io$/.test(domainMatch.groups['domain']) &&
          urlBid === match[2])
      ) {
        // https://buk.io 인 경우 새로운 창에서 메인 페이지로 이동하도록 분기처리
        if (memoUrl.replace(match[1], '') === '/') {
          return;
        }

        // 조건에 만족할경우 a 태그 기본 동작 방지 및 앵귤러 navigate로 새로고침 없이 이동하도록 수정
        event.preventDefault();

        this.eventBusService.fire('AnnotationsComponent:itemClick', {
          url: memoUrl.replace(match[1], ''),
          source: 'memo',
        });
      }
    }

    return;
  }
  @Input() menu?: TemplateRef<unknown>;
  @Input() menuData?: unknown;

  @Input() limitHeight = false;
  @Input() createdAt: string = '';
  @Input() updatedAt: string = '';
  @Input() isSingleMode = false;
  @Input() replyCommentCount = 0;
  @Input() isReplyCommentListOpen = false;
  @Input() isReplyCommentInputOpen = false;

  public _shortContent?: string;
  private _content!: string;

  // public isReplyCommentOpen = false; // 이 줄 제거

  @Output() contentClick = new EventEmitter<void>();
  @Output() replyCommentOpened = new EventEmitter<void>();

  public _menuPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];

  @Input() set content(value: string) {
    this._content = value;

    let lines = this._content.split('\n');

    if (lines.length > 3) {
      lines = lines.slice(0, 3);
      this._shortContent = lines.join('\n').slice(0, 64);
    } else if (this._content.length > 64) {
      this._shortContent = this._content.slice(0, 64);
    } else {
      this._shortContent = undefined;
    }
  }

  get content(): string {
    return this._content;
  }

  constructor(
    private router: Router,
    private eventBusService: EventBusService
  ) {}

  public toggleReplyComment(): void {
    this.replyCommentOpened.emit();
  }

  // 간략히 클릭시 기존 간략히 줄인 내용으로 돌리는 함수
  public changeShortContent(): void {
    let lines = this._content.split('\n');

    if (lines.length > 3) {
      lines = lines.slice(0, 3);
      this._shortContent = lines.join('\n').slice(0, 64);
    } else if (this._content.length > 64) {
      this._shortContent = this._content.slice(0, 64);
    } else {
      this._shortContent = undefined;
    }
  }
}
